import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { GenerateRoofDormerOptions, RoofDormer } from "building-types/roofs/roofGenerationTypes";
import type { RoofDormerPlacement } from "building-types/roofs/roofSystemTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import { BuildingSide, RoofSide } from "building-types/settings/roofTypes";
import type { RWTable, Table } from "common/data/table";
import type { Face } from "geom/brep";
import { polyLine2OnPlane } from "geom/plane";
import { polyLine2AABB2, polyLine2FromAABB2 } from "geom/polyLine2";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3Edge } from "geom/polyLine3";
import { roofFaceBuildingSide } from "../roofFaceBuildingSide";
import { getFinalDormerSettings } from "../roofs";
import { addDormers } from "../roofTools/multiGableRoofTools";
import { getMergedDormerSettings } from "./getMergedRoofSettings";

/**
 * Generates the dormers attached to this roof
 */
export async function buildRoofDormers(
  index: number,
  roofSystemId: string,
  subRoofDormerOptions: GenerateRoofDormerOptions,
  subRoof: GeneratedRoof,
  generatedDormerRoofVolumes: RWTable<GeneratedRoofVolumeLegacy>,
  generatedDormerRoofs: RWTable<GeneratedRoof>,
  entitySettings,
  activeSettingsSets,
  optionChoices,
  settings,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
): Promise<{ dormers: Table<RoofDormer>; index: number }> {
  const {
    dormerInputSettings,
    clipContext,
    level,
    mirror,
    windows,
    windowUnits,
    windowShutters,
    windowCatalog,
    addRoofWallFaces,
    dormerPlacements = [],
    issues = [],
  } = subRoofDormerOptions;

  const { dormerSettings, roofPlanSettings, roofElevationPlanSettings } = dormerInputSettings;
  const roofParentSettings = { roofPlanSettings, roofElevationPlanSettings };

  const { roofVolume, rails } = subRoof;
  const { extrusion, verticalRails } = roofVolume;
  const { isStartTruncated, isEndTruncated, isStartFullyTruncated, isEndFullyTruncated } = verticalRails;
  const { leftRail, rightRail, startRail, endRail } = rails;
  const { leftFaces, rightFaces, startHipFaces, endHipFaces } = extrusion;

  const dormers: RWTable<RoofDormer> = {};

  // helper function to add dormers to a side
  const tryToAddDormers = async (
    dormer: RoofDormerPlacement,
    roofSide: RoofSide,
    roofIndex: number,
  ): Promise<number> => {
    const mergedDormerSettings = getMergedDormerSettings({
      dormer,
      dormerSettings,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings,
    });

    const finalDormerSettings = getFinalDormerSettings(dormer.dormerSettings, mergedDormerSettings);
    const { side = BuildingSide.FRONT } = finalDormerSettings;

    let faces: Face[] = [];
    let rail3: PolyLine3 | undefined;

    switch (roofSide) {
      case RoofSide.LEFT:
        faces = leftFaces;
        rail3 = leftRail;
        break;
      case RoofSide.RIGHT:
        faces = rightFaces;
        rail3 = rightRail;
        break;
      case RoofSide.START:
        faces = startHipFaces;
        rail3 = startRail;

        if (isStartTruncated && !isStartFullyTruncated) {
          const { loops, plane } = startHipFaces[0];

          const aabb = polyLine2AABB2(loops[0]);
          rail3 = polyLine3Edge(polyLine2OnPlane(plane, polyLine2FromAABB2(aabb)), 0);
        }

        break;
      case RoofSide.END:
        faces = endHipFaces;
        rail3 = endRail;

        if (isEndTruncated && !isEndFullyTruncated) {
          const { loops, plane } = endHipFaces[0];

          const aabb = polyLine2AABB2(loops[0]);
          rail3 = polyLine3Edge(polyLine2OnPlane(plane, polyLine2FromAABB2(aabb)), 0);
        }

        break;
      default:
        break;
    }

    if (faces.length === 0 || !rail3) {
      return roofIndex;
    }

    const faceSide = roofFaceBuildingSide(faces[0], mirror);

    if (faceSide !== side) {
      return roofIndex;
    }

    const face = faces[0];

    return await addDormers({
      dormerSettings: finalDormerSettings,
      roofIndex,
      roofSystemId,
      level,
      clipContext,
      subRoof,
      side: roofSide,
      rail3,
      face,
      faceSide,
      mirror: !!mirror,
      dormer,
      dormerInputSettings,
      roofParentSettings,
      dormers,
      windowCatalog,
      windows,
      windowUnits,
      windowShutters,
      addRoofWallFaces,
      issues,
      generatedDormerRoofVolumes,
      generatedDormerRoofs,
      entitySettings,
      activeSettingsSets,
      optionChoices,
      settings,
      entityHandleForSelectingReportingUnit,
    });
  };

  for (const dormer of dormerPlacements) {
    index = await tryToAddDormers(dormer, RoofSide.LEFT, index);
    index = await tryToAddDormers(dormer, RoofSide.RIGHT, index);
    index = await tryToAddDormers(dormer, RoofSide.START, index);
    index = await tryToAddDormers(dormer, RoofSide.END, index);
  }

  return {
    dormers,
    index,
  };
}
